/**
 * adminReconciliationStore — Payment reconciliation slice of the admin store.
 *
 * Endpoints:
 *   GET  /api/reconciliation/issues   (bare array)
 *   POST /api/reconciliation/run      (run counters)
 */
import { API_URL } from "../../constants";
import { toDisplayString } from "../../utils/toDisplayString";
import { authHeaders, resolveToken } from "./adminUtils";

export const reconciliationInitialState = {
  reconciliationIssues: [],
  reconciliationLoading: false,
  reconciliationError: null,
  reconciliationRun: null,
  reconciliationMutationLoading: false,
  reconciliationMutationError: null,
};

async function apiRequest(get, path, { method = "GET", body } = {}) {
  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers: authHeaders(resolveToken(get)),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  const text = await response.text();
  let data = {};
  if (text) {
    const contentType = response.headers.get("content-type") || "";
    const trimmed = text.trim();
    if (contentType.includes("application/json") || trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        data = JSON.parse(text);
      } catch {
        data = {};
      }
    }
  }

  if (!response.ok || (data && data.error)) {
    throw new Error(
      toDisplayString(data?.error ?? data?.message, `Request failed (${response.status})`)
    );
  }

  return data;
}

export function createReconciliationActions(set, get) {
  return {
    fetchReconciliationIssues: async () => {
      set({ reconciliationLoading: true, reconciliationError: null });
      try {
        const data = await apiRequest(get, "/api/reconciliation/issues");
        const issues = Array.isArray(data) ? data : data?.data || data?.issues || [];
        set({ reconciliationLoading: false, reconciliationIssues: issues });
        return { success: true, issues };
      } catch (err) {
        const message = toDisplayString(err.message, "Failed to fetch reconciliation issues");
        set({ reconciliationLoading: false, reconciliationError: message });
        return { success: false, error: message };
      }
    },

    runReconciliation: async () => {
      set({ reconciliationMutationLoading: true, reconciliationMutationError: null });
      try {
        const data = await apiRequest(get, "/api/reconciliation/run", {
          method: "POST",
        });
        const run = data?.data ?? data ?? null;
        set({
          reconciliationMutationLoading: false,
          reconciliationMutationError: null,
          reconciliationRun: run,
          successMessage: "Reconciliation completed",
        });
        return { success: true, run };
      } catch (err) {
        const message = toDisplayString(err.message, "Failed to run reconciliation");
        set({ reconciliationMutationLoading: false, reconciliationMutationError: message });
        return { success: false, error: message };
      }
    },
  };
}

const reconciliationActions = createReconciliationActions;
export default reconciliationActions;
